import { createContext, useState, useEffect, useCallback } from 'react';
import { isLoggedIn, checkLoginState, clearTokenInvalidFlag, pointsApi } from '../api.js';

export const AuthContext = createContext(null);

// 登录态判断：调中枢 /api/auth/me（经本端 /api/auth/cookie-check 同源代理）。
// 200 → 已登录；401 → 未登录（跳中枢登录）；503/网络错 → 中枢不可达（不跳登录，显示"稍后再试"）。
let currentOnTokenInvalid = null;
export function setOnTokenInvalid(fn) { currentOnTokenInvalid = fn; }

const useLocalDebug = new URL(window.location.href).searchParams.get('local') === '1';

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [hubUnreachable, setHubUnreachable] = useState(false);

  useEffect(() => {
    void (async () => {
      setValidating(true);
      if (useLocalDebug) {
        setTokenState(null);
        setValidating(false);
        setLoading(false);
        return;
      }
      try {
        const state = await checkLoginState();
        if (state === 'loggedIn') {
          setTokenState('authed');
          setHubUnreachable(false);
          clearTokenInvalidFlag();
        } else if (state === 'notLoggedIn') {
          setTokenState(null);
          setHubUnreachable(false);
          clearTokenInvalidFlag();
        } else {
          // 'unknown' = 中枢不可达（503/网络错）：不跳登录，标记中枢暂不可用
          setTokenState(null);
          setHubUnreachable(true);
          clearTokenInvalidFlag();
        }
      } catch {
        setTokenState(null);
        setHubUnreachable(true);
        clearTokenInvalidFlag();
      } finally {
        setValidating(false);
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async () => {
    const ok = await isLoggedIn();
    setTokenState(ok ? 'authed' : null);
    setHubUnreachable(false);
  }, []);

  const logout = useCallback(async () => {
    clearTokenInvalidFlag();
    setTokenState(null);
    currentOnTokenInvalid = null;
  }, []);

  const value = { token, loading, validating, hubUnreachable, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
